import React from 'react';
import {Header} from 'semantic-ui-react';
import {connectDynamic} from '../../dynamicComponent';
import {Alert} from 'componentLibrary';
import {
  DynamicRadioFieldConnected as DynamicRadioField,
  DynamicTextBoxFieldConnected as DynamicTextBoxField,
} from 'componentLibrary';
import {FemaGoTable} from 'app-ui-componentlibrary';
import _ from 'lodash';
import {safeAdd} from 'common/utils';
import {produce} from 'immer';

const VEHICLE_TYPES_DESCRIPTION = {
  engines: 'Engine or pumper',
  ambulancesTransport: 'Ambulance',
  tankers: 'Tanker or tender',
  aerial: 'Aerial apparatus',
  brushQuickAttack: 'Brush/quick attack',
  rescue: 'Rescue vehicle',
  additional: 'Additional vehicle',
  bariatricAmbulances: 'Bariatric Ambulances',
  nonTransport: 'Non-transport - Community Paramedic',
};

const THRESHOLD = 15;

const DEFAULT_BELOW_THRESHOLD_ALERT =
  'Please provide the Model year, pumping capacity, and carrying capacity for each vehicle within your organization\'s inventory. The list of vehicles has been prepopulated based on your inputs to the <a href="#Applicant and community trends">Applicant and community trends</a> section of this application.';
const EMS_BELOW_THRESHOLD_ALERT =
  'Please provide the Model year, and usage for transport for each vehicle within your organization\'s inventory. The list of vehicles has been prepopulated based on your inputs to the <a href="#Applicant and community trends">Applicant and community trends</a> section of this application.';
const DEFAULT_ABOVE_THRESHOLD_ALERT =
  'Please provide the oldest model year, newest model year, and average model year of the vehicles in number of years within your organization\'s inventory. The types and quantities of vehicles have been prepopulated based on your inputs to the <a href="#Applicant and community trends">Applicant and community trends</a> section of this application.';

const DEFAULT_BELOW_THRESHOLD_COLUMNS = [
  'Vehicle type or class',
  'Model year (e.g. 2009)',
  'Pumping capacity (gallons per minute)',
  'Carrying capacity (gallons)',
];
const EMS_BELOW_THRESHOLD_COLUMNS = [
  'Vehicle type or class',
  'Model year (e.g. 2009)',
  'Used for transport',
];
const DEFAULT_ABOVE_THRESHOLD_COLUMNS = [
  'Vehicle type or class',
  'Quantity',
  'Oldest model year (e.g. 2009)',
  'Newest model year (e.g. 2009)',
  'Average model year (e.g. 1995)',
];

const DEFAULT_BELOW_THRESHOLD_FIELDS = ['age', 'pumpingCapacity', 'carryingCapacity'];
const EMS_BELOW_THRESHOLD_FIELDS = ['age', 'usedForTransport'];
const DEFAULT_ABOVE_THRESHOLD_FIELDS = [
  'quantity',
  'oldestAge',
  'newestAge',
  'averageAge',
];

const FIELD_TYPE = {
  age: {
    type: 'DynamicTextBoxField',
    maxLength: 5,
  },
  usedForTransport: {
    type: 'DynamicRadioField',
  },
  pumpingCapacity: {
    type: 'DynamicTextBoxField',
    maxLength: 9,
  },
  carryingCapacity: {
    type: 'DynamicTextBoxField',
    maxLength: 9,
  },
  quantity: {
    type: 'QuantityText',
  },
  oldestAge: {
    type: 'DynamicTextBoxField',
    maxLength: 5,
  },
  newestAge: {
    type: 'DynamicTextBoxField',
    maxLength: 5,
  },
  averageAge: {
    type: 'DynamicTextBoxField',
    maxLength: 5,
  },
};

const EMS_APPLICANT = 'Nonaffiliated EMS Organization';

export class VehicleInventoryTable extends React.Component {
  state = {};
  componentDidMount() {
    this.refresh();
  }

  componentDidUpdate(prevProps, prevState) {
    if (!_.isEqual(this.props.vehicleTypeBreakdown, prevProps.vehicleTypeBreakdown)) {
      this.refresh(prevState);
    }
  }

  refresh(prevState) {
    const {data, vehicleTypeBreakdown} = this.props;
    let allVehicles = {};
    let totalVehicleCount = 0;
    _.forEach(vehicleTypeBreakdown, (vehicles, type) => {
      allVehicles[type] = safeAdd(
        vehicles.frontline,
        vehicles.reserve,
        vehicles.numberOfApparatus
      );
      totalVehicleCount += allVehicles[type];
    });
    const previousVehicleCount = _.get(prevState, 'totalVehicleCount', totalVehicleCount);
    let newData = {};
    if (totalVehicleCount <= THRESHOLD) {
      _.forEach(_.keys(allVehicles), (key) => {
        const existingRows = _.get(data, key);
        newData[key] =
          previousVehicleCount > THRESHOLD
            ? _.times(allVehicles[key], (i) => {})
            : _.times(allVehicles[key], (i) => _.get(existingRows, i, {}));
      });
    } else {
      _.forEach(_.keys(allVehicles), (key) => {
        let newRow = previousVehicleCount <= THRESHOLD ? {} : _.get(data, key, {});
        if (_.get(allVehicles, key) > 0) {
          newData[key] = produce(newRow, (draft) => {
            draft.quantity = _.get(allVehicles, key);
          });
        }
      });
    }
    if (!_.isEqual(this.state.data, newData)) {
      this.setState({data: newData, totalVehicleCount}, this.handleBlur);
    }
  }

  handleBlur = (e) => {
    const {saveEntityData, entityType, entityDataPath, readOnly} = this.props;
    const {data} = this.state;

    // We don't want to trigger the saveEntity saga when we are in ready only mode
    !readOnly &&
      saveEntityData(entityDataPath, entityType, data, _.get(e, 'target.name'));
  };

  handleChange = (e) => {
    const {data} = this.state;
    this.setState({
      data: produce(data, (draft) => _.set(draft, e.target.name, e.target.value)),
    });
  };

  findError = (errArr) => {
    let errToReturn = 0;
    for (let x = 0; x < _.size(errArr); x++) {
      if (errArr[x].isValid === false) {
        errToReturn = x;
      }
    }
    return _.get(errArr, errToReturn);
  };

  getTableColumns = (applicantType, totalVehiclesNum) => {
    if (totalVehiclesNum <= THRESHOLD) {
      if (_.isEqual(applicantType, EMS_APPLICANT)) {
        return EMS_BELOW_THRESHOLD_COLUMNS;
      } else {
        return DEFAULT_BELOW_THRESHOLD_COLUMNS;
      }
    } else {
      return DEFAULT_ABOVE_THRESHOLD_COLUMNS;
    }
  };

  getTableFields = (applicantType, totalVehiclesNum) => {
    if (totalVehiclesNum <= THRESHOLD) {
      if (_.isEqual(applicantType, EMS_APPLICANT)) {
        return EMS_BELOW_THRESHOLD_FIELDS;
      } else {
        return DEFAULT_BELOW_THRESHOLD_FIELDS;
      }
    } else {
      return DEFAULT_ABOVE_THRESHOLD_FIELDS;
    }
  };

  renderAlert = (applicantType, totalVehiclesNum) => {
    if (totalVehiclesNum <= THRESHOLD) {
      if (_.isEqual(applicantType, EMS_APPLICANT)) {
        return (
          <Alert header="" htmlContent={EMS_BELOW_THRESHOLD_ALERT} alertType="info" />
        );
      } else {
        return (
          <Alert header="" htmlContent={DEFAULT_BELOW_THRESHOLD_ALERT} alertType="info" />
        );
      }
    } else {
      return (
        <Alert header="" htmlContent={DEFAULT_ABOVE_THRESHOLD_ALERT} alertType="info" />
      );
    }
  };

  renderTextField = (vehicleType, vehicleTypeDetail, fieldName, rowIndex) => {
    const {rootPath, readOnly, validationId} = this.props;
    const fieldFullName = `${vehicleType}.${
      rowIndex !== undefined ? rowIndex + '.' : ''
    }${fieldName}`;
    const completeFullName = validationId + '.' + fieldFullName;
    return (
      <FemaGoTable.TextCell>
        {readOnly ? (
          <React.Fragment>{_.get(vehicleTypeDetail, fieldName)}</React.Fragment>
        ) : (
          <DynamicTextBoxField
            id={completeFullName}
            size="large"
            entityType="data"
            entityDataPath={`${
              rootPath + '.additionalInformation.vehicleInventory.' + fieldFullName
            }`}
            maxLength={FIELD_TYPE[fieldName].maxLength}
            validationId={completeFullName}
          />
        )}
      </FemaGoTable.TextCell>
    );
  };

  renderRadioField = (vehicleType, vehicleTypeDetail, fieldName, rowIndex) => {
    const {rootPath, readOnly, validationId} = this.props;
    const fieldFullName = vehicleType + '.' + rowIndex + '.' + fieldName;
    const completeFullName =
      validationId + '.' + vehicleType + '.' + rowIndex + '.' + fieldName;
    return (
      <FemaGoTable.TextCell>
        {readOnly ? (
          <React.Fragment>{_.get(vehicleTypeDetail, fieldName)}</React.Fragment>
        ) : (
          <DynamicRadioField
            id={completeFullName}
            size="large"
            entityType="data"
            entityDataPath={`${
              rootPath + '.additionalInformation.vehicleInventory.' + fieldFullName
            }`}
            validationId={completeFullName}
            options={[
              {label: 'Yes', value: 'Yes'},
              {label: 'No', value: 'No'},
            ]}
          />
        )}
      </FemaGoTable.TextCell>
    );
  };

  renderQuantityText = (vehicleType) => {
    const {data} = this.props;
    return (
      <FemaGoTable.TextCell>
        {_.get(data, [vehicleType, 'quantity'])}
      </FemaGoTable.TextCell>
    );
  };

  renderVehicleTypeInfoRows = (vehicleType, vehicleTypeDetail, rowIndex) => {
    const {applicantType} = this.props;
    const {totalVehicleCount} = this.state;
    const tableFields = this.getTableFields(applicantType, totalVehicleCount);
    return (
      <FemaGoTable.Row key={`${vehicleType}Row${rowIndex}`}>
        <FemaGoTable.TextCell>
          {VEHICLE_TYPES_DESCRIPTION[vehicleType]}
        </FemaGoTable.TextCell>
        {_.map(tableFields, (fieldName, i) => {
          return _.isEqual(FIELD_TYPE[fieldName].type, 'DynamicRadioField')
            ? this.renderRadioField(vehicleType, vehicleTypeDetail, fieldName, rowIndex)
            : _.isEqual(FIELD_TYPE[fieldName].type, 'QuantityText')
            ? this.renderQuantityText(vehicleType, rowIndex)
            : this.renderTextField(vehicleType, vehicleTypeDetail, fieldName, rowIndex);
        })}
      </FemaGoTable.Row>
    );
  };

  renderVehicleTypeInfo = (vehicleType) => {
    const {data, totalVehicleCount} = this.state;
    const vehicleTypeData = _.get(data, vehicleType);
    const rows =
      totalVehicleCount > THRESHOLD
        ? this.renderVehicleTypeInfoRows(vehicleType, vehicleTypeData)
        : _.map(vehicleTypeData, (vehicleTypeDetail, rowIndex) =>
            this.renderVehicleTypeInfoRows(vehicleType, vehicleTypeDetail, rowIndex)
          );
    return rows;
  };

  render() {
    const {applicantType, data} = this.props;
    const {totalVehicleCount} = this.state;
    const tableColumns = this.getTableColumns(applicantType, totalVehicleCount);

    return (
      <React.Fragment>
        <Header as="h4">Vehicle inventory</Header>
        {this.renderAlert(applicantType, totalVehicleCount)}
        <FemaGoTable
          type="static"
          columns={tableColumns.length}
          withForm={false}
          summary="Vehicle Inventory"
        >
          <FemaGoTable.Header>
            <FemaGoTable.Row key="vehicleInventoryTableHeader">
              {_.map(tableColumns, (columnName, i) => {
                return (
                  <FemaGoTable.HeaderCell key={`${columnName}${i}`}>
                    {columnName}
                  </FemaGoTable.HeaderCell>
                );
              })}
            </FemaGoTable.Row>
          </FemaGoTable.Header>

          <FemaGoTable.Body>
            {_.map(_.keys(data), (vehicleType) =>
              this.renderVehicleTypeInfo(vehicleType)
            )}
          </FemaGoTable.Body>
        </FemaGoTable>
      </React.Fragment>
    );
  }
}

export default connectDynamic(VehicleInventoryTable);
